// Custom DS-Table column rendering the Quote Status: a design-system Pill per quote status, plain
// "No quote" text when the project has none (the public column API has no arbitrary-JSX cell, so we
// author a column following the established DS pattern — see StatusPillColumn — and import the two
// cell/column-support helpers from the dist internals, the accepted web-front convention).
//
// The pills are outlined rather than filled because the filled palette has no error colour: Pill's
// `variant` covers highlight/success/neutral/information only, and red lives solely on
// `outlinedVariant`. A declined quote has to read as an error, so every status here uses the
// outlined form to keep one visual style across the column.
import React from 'react';
import type {ColumnDef} from '@tanstack/react-table';
import {Pill, OutlinedPillVariant} from '@forecast-it/design-system';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {defaultColumnDef} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/column/support/BaseColumn';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {StyledCell, Align} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/cell/Cell.styled';
import type {OpportunityTableRow} from '../opportunityTableRows';

export interface QuoteStatusColumnType {
	id?: string;
	accessorKey: string;
	header: string;
	visible?: boolean;
	align?: Align;
	'data-cy'?: string;
	/** Localized label for a published quote's pill. */
	publishedLabel: string;
	/** Localized label for an approved quote's pill. */
	approvedLabel: string;
	/** Localized label for a declined quote's pill. */
	declinedLabel: string;
	/** Localized label for a quote being revised. */
	revisingLabel: string;
	/** Localized text for a project without a quote. */
	noQuoteLabel: string;
	width?: string;
}

// Config-only component: DS Table consumes its props via toColumnDef and never renders it.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const QuoteStatusColumn = (_: QuoteStatusColumnType): null => null;

/**
 * The pill to draw for a quote status, or null when the cell should fall back to plain text.
 *
 * Only the statuses a quote can actually hold today are mapped. Anything else — including a project
 * with no quote at all — falls back to the no-quote text, so a status that becomes reachable later
 * must be added here at the same time: sending a quote and expiry both write statuses this column
 * would otherwise render as "No quote", which would be wrong rather than merely missing.
 */
const pillFor = (
	quoteStatus: string | null,
	props: QuoteStatusColumnType
): {label: string; variant: OutlinedPillVariant} | null => {
	switch (quoteStatus) {
		case 'PUBLISHED':
			return {label: props.publishedLabel, variant: OutlinedPillVariant.NEUTRAL};
		case 'APPROVED':
			return {label: props.approvedLabel, variant: OutlinedPillVariant.SUCCESS};
		case 'DECLINED':
			return {label: props.declinedLabel, variant: OutlinedPillVariant.ERROR};
		// ACTIVE, not SUCCESS or ERROR: a revise is work in progress, and the outcome it replaced is
		// no longer what the column should report.
		case 'REVISING':
			return {label: props.revisingLabel, variant: OutlinedPillVariant.ACTIVE};
		default:
			return null;
	}
};

QuoteStatusColumn.toColumnDef = (child: React.ReactElement<QuoteStatusColumnType>): ColumnDef<OpportunityTableRow> => ({
	...defaultColumnDef(child.props),
	accessorKey: child.props.accessorKey,
	cell: ({row}: {row: {original: OpportunityTableRow}}) => {
		const pill = pillFor(row.original.quoteStatus, child.props);
		return (
			<StyledCell align={child.props.align} data-cy="opportunity-quote-status-cell">
				{pill ? (
					<span data-cy="quote-status-pill">
						<Pill outlined outlinedVariant={pill.variant}>
							{pill.label}
						</Pill>
					</span>
				) : (
					child.props.noQuoteLabel
				)}
			</StyledCell>
		);
	},
});

export default QuoteStatusColumn;
